import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';

// Shared toast options for lineup-action errors — matches the existing
// patterns in V3FinancePage and useV3OfferAutosave.
const ERROR_TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  autoClose: 5000,
  hideProgressBar: true,
  draggable: false,
};

// Hook that owns the lineup-rail action handlers previously inlined in
// V3FinancePage:
//
//   * handleSelectOffer    — clicking a sibling card → fetch + dispatch the
//                            new offer, with abort-on-rapid-switch and
//                            stale-response guards.
//   * handleReorder        — drag-reorder the lineup rail with optimistic
//                            updates + version-aware rollback so a stale
//                            failure can't clobber a newer drag.
//   * handlePickNewArtist  — POST a new v3_offer for the picked/created
//                            artist, then load it in place.
//
// The hook also wires up the popstate listener so back/forward browser
// navigation re-fetches the matching offer through handleSelectOffer.
//
// Modal-orchestration (send-offer, link-offer, pro-forma) stays in the
// page component because it owns the modal's open/close state. The hook
// returns only the imperative action functions and lets the page wire
// them into the JSX.
export default function useV3LineupActions() {
  const { csrfToken, team, offer, dispatch } = useV3OfferContext();

  // ── handleSelectOffer ─────────────────────────────────────────────────
  //
  // In-flight guard: cancels the previous fetch when the user clicks a new
  // sibling card mid-load, and discards stale responses that resolve after
  // the user has already moved on. Only the latest-requested offer ever
  // makes it into the context.
  const selectAbortControllerRef = useRef(null);
  const latestSelectIdRef = useRef(null);
  // Exposed to consumers (lineup strip) so the tapped chip can show its
  // spinner overlay and the offer card below can dim, between the moment
  // the user picks a sibling and the moment the new offer's data lands.
  const [selectingOfferId, setSelectingOfferId] = useState(null);

  const handleSelectOffer = useCallback(async (newOfferId) => {
    if (!newOfferId || newOfferId === offer?.id) return;

    // Cancel any prior in-flight fetch.
    if (selectAbortControllerRef.current) {
      selectAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    selectAbortControllerRef.current = controller;
    latestSelectIdRef.current = newOfferId;
    setSelectingOfferId(newOfferId);

    try {
      const { data } = await axios.get(
        `/teams/${team.id}/offers/v3/${newOfferId}.json`,
        { signal: controller.signal }
      );

      // If a newer click happened while this fetch was in flight, drop the
      // result — the latest click's dispatch wins.
      if (latestSelectIdRef.current !== newOfferId) return;

      // data is the offer blueprint (:detail view).
      // Unpack the nested budget + line items and dispatch each slice.
      const newBudget = data.v3_budget || null;
      const newLineItems = newBudget?.v3_budget_line_items || [];
      const newDealBrackets = newBudget?.v3_deal_brackets || [];

      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: data });

      if (newBudget) {
        const budgetFields = { ...newBudget };
        delete budgetFields.v3_budget_line_items;
        delete budgetFields.v3_deal_brackets;
        // Mirror what the SSR edit action does: guarantee_notes lives on the
        // offer record but the budget hash carries it for legacy consumers.
        budgetFields.guarantee_notes = data.guarantee_notes;
        dispatch({ type: V3_ACTIONS.SET_BUDGET, payload: budgetFields });
      }

      dispatch({ type: V3_ACTIONS.SET_LINE_ITEMS, payload: newLineItems });
      dispatch({ type: V3_ACTIONS.SET_DEAL_BRACKETS, payload: newDealBrackets });
      // Refresh sibling state so the Expenses table picks up newly-added
      // siblings without a full page reload (O-2993). The :detail blueprint
      // returns event_offers in the same shape as the SSR'd page prop.
      if (Array.isArray(data?.event_offers)) {
        dispatch({ type: V3_ACTIONS.SET_EVENT_OFFERS, payload: data.event_offers });
      }

      window.history.pushState(
        {},
        '',
        `/teams/${team.id}/offers/v3/${newOfferId}/edit`
      );
    } catch (err) {
      // Aborted fetches throw; ignore them — a newer fetch is in flight.
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      toast.error('Could not load that offer', ERROR_TOAST_OPTS);
    } finally {
      // Clear the controller ref only if it's still ours.
      if (selectAbortControllerRef.current === controller) {
        selectAbortControllerRef.current = null;
      }
      // Same staleness guard as the dispatch above: only clear the loading
      // marker when we're still the most-recent click.
      if (latestSelectIdRef.current === newOfferId) {
        setSelectingOfferId(null);
      }
    }
  }, [team.id, offer?.id, dispatch]);

  // Cancel any pending offer-switch fetch on unmount.
  useEffect(() => () => {
    if (selectAbortControllerRef.current) {
      selectAbortControllerRef.current.abort();
    }
  }, []);

  // Sync the pane when the user navigates back/forward via browser history.
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/\/offers\/v3\/(\d+)\/edit/);
      if (match) {
        const offerId = parseInt(match[1], 10);
        if (offerId !== offer?.id) handleSelectOffer(offerId);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [offer?.id, handleSelectOffer]);

  // ── handleReorder ─────────────────────────────────────────────────────
  //
  // Tracks the most-recent reorder request so stale failures don't clobber
  // newer optimistic state. If the user drags twice in quick succession and
  // drag #1's PATCH rejects after drag #2 has already dispatched its own
  // optimistic update, restoring drag #1's snapshot would erase drag #2's
  // (correct) state. We only roll back when our version is still the latest.
  const reorderVersionRef = useRef(0);

  const handleReorder = useCallback(async (orderedIds) => {
    const calendarEventId = offer?.calendar_event_id;
    if (!calendarEventId) return;

    const myVersion = ++reorderVersionRef.current;

    // Snapshot before mutating so we can roll back on failure.
    const previousLineup = offer.lineup;

    // Optimistic update — re-index supports by their new position in orderedIds.
    const optimisticLineup = (offer.lineup || []).map((o) => {
      const newPos = orderedIds.indexOf(o.id);
      if (newPos === -1) return o; // headliner or unrelated
      return { ...o, position: newPos };
    });
    dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { lineup: optimisticLineup } });

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.patch(`/teams/${team.id}/offers/v3/reorder`, {
        calendar_event_id: calendarEventId,
        ordered_ids: orderedIds,
      });
    } catch (err) {
      // Only roll back if a newer drag hasn't superseded us. If it has, its
      // optimistic state is the user's intent — leave it alone.
      if (reorderVersionRef.current === myVersion) {
        dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { lineup: previousLineup } });
      }
      toast.error('Failed to reorder lineup', ERROR_TOAST_OPTS);
    }
  }, [offer, team.id, csrfToken, dispatch]);

  // "+ Add artist": a shared-picker submission (existing pick or inline
  // create) POSTs a sibling v3_offer with primary_performer_attributes; on
  // success the new offer becomes the active one via the same fetch+SET_OFFER
  // path used for switching siblings.
  const handlePickNewArtist = useCallback(async ({ artistId, newArtistName, actType, spotifyId, externalUrl, spotifyTakeover }) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const resolvedActType = actType === 'headline' ? 'headline' : 'support';
    const performerAttributes = newArtistName
      ? {
        act_type: resolvedActType,
        artistable_type: 'Artist',
        artist_attributes: {
          name: newArtistName,
          ...(spotifyId ? { spotify_id: spotifyId } : {}),
          ...(externalUrl ? { external_url: externalUrl } : {}),
          ...(spotifyTakeover ? { spotify_takeover: true } : {}),
        },
      }
      : {
        act_type: resolvedActType,
        artistable_type: 'Artist',
        artistable_id: artistId,
      };

    const v3OfferPayload = {
      calendar_event_id: offer?.calendar_event_id || offer?.calendar_event?.id,
      v3_budget_id: offer?.v3_budget_id,
      venue_id: offer?.venue_id || offer?.venue?.id,
      pipeline_id: offer?.pipeline_id,
      event_name: offer?.event_name || '',
      dollar_amount: 0,
      upside_percentage: 0,
      dollar_or_percentage_operator: 'AND',
      from_dollar_one: true,
      // O-2783: if the current event is in a pool, the new artist JOINS it (so
      // the event stays in pool mode after the add). It inherits the default
      // share — explicit 0% — just like the first artist; the booker sets the %
      // in the pool UI. Without this the new offer is independent and the whole
      // event drops out of pool mode on the next load.
      ...(offer?.v3_offer_pool_id ? { v3_offer_pool_id: offer.v3_offer_pool_id } : {}),
      primary_performer_attributes: performerAttributes,
    };

    let newOffer;
    try {
      ({ data: newOffer } = await axios.post(
        `/teams/${team.id}/offers/v3`,
        { v3_offer: v3OfferPayload },
      ));
    } catch (e) {
      const detail = Array.isArray(e?.response?.data)
        ? e.response.data.join(', ')
        : (typeof e?.response?.data === 'string' ? e.response.data : null);
      toast.error(detail ? `Failed to add artist: ${detail}` : 'Failed to add artist', ERROR_TOAST_OPTS);
      return false;
    }

    if (!newOffer?.id) {
      // A 2xx with no id means nothing was actually created — don't report
      // success (which would close the modal and discard the user's input).
      toast.error('Failed to add artist', ERROR_TOAST_OPTS);
      return false;
    }

    // In-place: load the new offer via the same fetch+SET_OFFER path used for
    // switching siblings. NO full-page reload — that re-ran the new-offer FTUX
    // (template modal) and discarded any in-flight pool-deal save (O-2783).
    // The offer is already committed at this point, so a failed load must not
    // read as a failed add — a retry would create a duplicate sibling.
    try {
      await handleSelectOffer(newOffer.id);
    } catch (e) {
      toast.warn('Artist added — refresh to see the new offer', ERROR_TOAST_OPTS);
    }
    return true;
  }, [team, offer, csrfToken, handleSelectOffer]);

  return {
    handleSelectOffer,
    handleReorder,
    handlePickNewArtist,
    selectingOfferId,
  };
}
